import { useEffect, useState } from 'react';
import type { ListingCard, ListingKind } from '@dunea/shared';
import { apiListListings, type ListingSort } from '~/lib/api/marketplace';
import { useResource } from '~/components/workbench/tools/payments/use-resource';

export interface ListingFilters {
  q: string;
  category: string;
  sort: ListingSort;
}

/** `value`, settled for `ms` (search-as-you-type without a request per key). */
export function useDebounced<T>(value: T, ms = 300): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);

  return settled;
}

/** The catalog for one kind + filter, page 1 on change, "Load more" appends. */
export function useListings(kind: ListingKind, filters: ListingFilters) {
  const q = useDebounced(filters.q);
  const key = `${kind}|${q}|${filters.category}|${filters.sort}`;
  const first = useResource(
    () => apiListListings({ kind, q, category: filters.category || undefined, sort: filters.sort }),
    key,
  );
  const [more, setMore] = useState<{ key: string; items: ListingCard[]; page: number; hasMore: boolean } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);

  const extra = more?.key === key ? more : null;
  const items = first.data ? [...first.data.items, ...(extra?.items ?? [])] : [];
  const hasMore = extra ? extra.hasMore : Boolean(first.data?.hasMore);

  const loadMore = async () => {
    const page = (extra?.page ?? 1) + 1;
    setLoadingMore(true);
    setMoreError(false);

    try {
      const next = await apiListListings({
        kind,
        q,
        category: filters.category || undefined,
        sort: filters.sort,
        page,
      });
      setMore({ key, items: [...(extra?.items ?? []), ...next.items], page, hasMore: next.hasMore });
    } catch (err) {
      console.error(err);
      setMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    items,
    total: first.data?.total ?? 0,
    loading: first.loading,
    error: first.error,
    reload: first.reload,
    hasMore,
    loadMore,
    loadingMore,
    moreError,
    /** True while the typed search hasn't been sent yet. */
    settling: q !== filters.q,
  };
}
